/**
 * BillingWarningBanner (R3 PR B)
 *
 * Shown on every authenticated page. There is no grace period: access stops
 * the moment a trial expires or a payment fails, so we warn ahead of time.
 *
 *  - trial_ends_today: last 24 hours of a valid trial
 *  - card_expires_soon: default card expires within 14 days (daily
 *    read-only Stripe check on the server)
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CreditCard, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useBilling } from "../context/BillingContext";

export default function BillingWarningBanner() {
  const { user } = useAuth();
  const { billing } = useBilling();
  const [dismissed, setDismissed] = useState({});

  if (!user || !billing) return null;

  const banners = [];

  if (billing.trial_ends_today && !dismissed.trial) {
    banners.push({
      key: "trial",
      tone: "bg-red-600 text-white",
      icon: AlertTriangle,
      text: "Your trial ends today, access stops at expiry.",
      cta: "Subscribe now",
      to: "/pricing",
    });
  }

  if (billing.card_expires_soon && !dismissed.card) {
    const mm = billing.card_exp_month ? String(billing.card_exp_month).padStart(2, "0") : "";
    const when = mm && billing.card_exp_year ? ` (${mm}/${billing.card_exp_year})` : "";
    banners.push({
      key: "card",
      tone: "bg-amber-500 text-slate-900",
      icon: CreditCard,
      text: `Your card on file expires soon${when}. Update it to avoid losing access.`,
      cta: "Update card",
      to: "/settings",
    });
  }

  if (banners.length === 0) return null;

  return (
    <div role="status" aria-live="polite" className="relative z-[60]">
      {banners.map(({ key, tone, icon: Icon, text, cta, to }) => (
        <div
          key={key}
          data-testid={`billing-warning-${key}`}
          className={`${tone} px-4 py-2 text-sm flex items-center justify-center gap-3`}>
          <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="font-medium">{text}</span>
          <Link
            to={to}
            className="underline underline-offset-2 font-semibold hover:opacity-80">
            {cta}
          </Link>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => setDismissed((d) => ({ ...d, [key]: true }))}
            className="ml-2 opacity-70 hover:opacity-100">
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  );
}
